import {computed, inject, Injectable, signal} from '@angular/core';
import {
  ApplyPriceCoefficientRequest,
  CreateProductRequest,
  ProductDetails,
  ProductService,
  ProductSummary,
  ProductType,
  UpdateProductRequest,
} from '../../../../api';
import {lastValueFrom} from 'rxjs';
import {injectMutation, injectQuery, QueryClient} from '@tanstack/angular-query-experimental';
import {QueryKey} from '../../../constants/query-key';

export interface ProductFilter {
  searchTerm: string | null;
  productTypes: ProductType[] | null;
  legalEntityIds: number[] | null;
}

@Injectable()
export class ProductStoreService {
  private readonly productService = inject(ProductService);
  private readonly queryClient = inject(QueryClient);

  selectedProductId = signal<number | null>(null);
  filterParams = signal<ProductFilter>({searchTerm: null, productTypes: null, legalEntityIds: null});

  private readonly productsQuery = injectQuery(() => ({
    queryKey: [QueryKey.PRODUCTS, this.filterParams()],
    queryFn: () => {
      const {searchTerm, productTypes, legalEntityIds} = this.filterParams();
      return lastValueFrom(this.productService.getProducts(
        searchTerm ?? '',
        productTypes ?? [],
        legalEntityIds ?? []
      ));
    },
  }));

  products = computed<ProductSummary[]>(() => this.productsQuery.data()?.products ?? []);
  isLoading = computed(() => this.productsQuery.isLoading());

  selectedProductQuery = injectQuery(() => ({
    queryKey: [QueryKey.PRODUCT, this.selectedProductId()],
    queryFn: () => lastValueFrom(this.productService.getProductById(this.selectedProductId()!)),
    enabled: this.selectedProductId() !== null,
    staleTime: 0,
  }));
  selectedProduct = computed<ProductDetails | null>(() => {
    if (this.selectedProductId() === null) return null;
    return this.selectedProductQuery.data()?.product ?? null;
  });
  isLoadingSelectedProduct = computed(() => this.selectedProductQuery.isLoading());

  createProductMutation = injectMutation(() => ({
    mutationFn: (request: CreateProductRequest) =>
      lastValueFrom(this.productService.createProduct(request)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCTS]});
    },
  }));

  updateProductMutation = injectMutation(() => ({
    mutationFn: (request: { id: number; body: UpdateProductRequest }) =>
      lastValueFrom(this.productService.updateProduct(request.id, request.body)),
    onSuccess: (_, request) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCTS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCT, request.id]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCT_SELECT]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCT_DETAILS]});
      this.invalidateInvoicingQueries();
    },
  }));

  deleteProductMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.productService.deleteProduct(id)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCTS]});
    },
  }));

  applyPriceCoefficientMutation = injectMutation(() => ({
    mutationFn: (request: ApplyPriceCoefficientRequest) =>
      lastValueFrom(this.productService.applyPriceCoefficient(request)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCTS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCT_SELECT]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCT_DETAILS]});
      this.invalidateInvoicingQueries();
    },
  }));

  // Modifier un produit resynchronise les factures brouillon côté back : prix, libellé et unité de
  // mesure des lignes générées, et suppression des lignes (voire du brouillon) devenues non facturables.
  private invalidateInvoicingQueries(): void {
    this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICES]});
    this.queryClient.invalidateQueries({queryKey: ['invoice']});
    this.queryClient.invalidateQueries({queryKey: ['invoicePdf']});
    this.queryClient.invalidateQueries({queryKey: ['invoiceAccountingOperations']});
    this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICING_PREVIEW]});
    // Une ligne supprimée libère sa période : elle revient dans l'historique et, pour un one-shot,
    // le produit client réapparaît dans la liste.
    this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCT_CUSTOMER_BILLINGS]});
    this.queryClient.invalidateQueries({queryKey: [QueryKey.PRODUCT_CUSTOMERS]});
  }
}
